// Dev harness — simulates the Single-SPA host shell so `npm run dev`
// boots the parcel through the same lifecycle production hosts use.
//
// In production the auction-bidder parcel mounts inside Neon CRM / Neon
// Fundraise. They register the parcel via Single-SPA, supply
// editableProps from `mfe.config.js`, and call bootstrap → mount. The
// harness here mirrors that flow against root-app.ts so the dev path
// hits the parcel API instead of the standalone main.ts entry — without
// the harness, lifecycle regressions only surface in production.
import { bootstrap, mount, unmount } from '@/root-app';
import { env } from '@/env';
import { devFallbackSlug } from '@/dev-fallback-slug';

interface HarnessProps {
  // Per services/bidder-web/mfe.config.js editableProps.
  name: string;
  eventSlug: string;
  apiBaseUrl: string;
  wsBaseUrl: string;
  bidderJwt?: string;
  themeTokens?: Record<string, string>;
  locale: string;
  timezone?: string;
  // single-spa-vue contract: mount target.
  domElement: HTMLElement;
}

const MOUNT_ID = 'single-spa-application:auction-bidder';

function ensureMountNode(): HTMLElement {
  let node = document.getElementById(MOUNT_ID);
  if (!node) {
    node = document.createElement('div');
    node.id = MOUNT_ID;
    document.body.appendChild(node);
  }
  return node;
}

function buildProps(): HarnessProps {
  return {
    name: 'auction-bidder',
    eventSlug: devFallbackSlug(),
    apiBaseUrl: env.apiBaseUrl,
    wsBaseUrl: env.wsBaseUrl,
    locale: 'en-US',
    domElement: ensureMountNode(),
  };
}

async function boot(): Promise<void> {
  const props = buildProps();
  // Single-SPA hosts call bootstrap once before the first mount.
  await bootstrap(props as unknown as Parameters<typeof bootstrap>[0]);
  await mount(props as unknown as Parameters<typeof mount>[0]);
  // If `/` is the entry, redirect into the seeded slug so a feature route
  // renders. The shell router handles this redirect for us — the harness
  // only needs to ensure the mount happens. We don't need to push the
  // location ourselves because main → router redirect chain runs on
  // mount.
}

// Expose unmount on `window` for ad-hoc dev verification ("does removing
// the host detach cleanly?"). Production hosts call this directly via
// Single-SPA.
declare global {
  interface Window {
    __auctionBidderUnmount?: () => Promise<unknown>;
  }
}
window.__auctionBidderUnmount = async () => {
  return unmount(buildProps() as unknown as Parameters<typeof unmount>[0]);
};

void boot();
